import React, { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import GlowingCard from '../ui/GlowingCard';

interface StepNavigationFooterProps {
  currentStep?: number; // Optional for backward compatibility
  totalSteps?: number; // Optional for backward compatibility
  onBack: () => void;
  onNext: () => void;
  canGoBack?: boolean;
  canGoNext?: boolean;
  isNextDisabled?: boolean; // Backward compatibility
  nextLabel?: string;
  backLabel?: string;
  isNextLoading?: boolean;
  isDarkMode?: boolean; // Backward compatibility
  className?: string;
}

const StepNavigationFooter: React.FC<StepNavigationFooterProps> = ({
  currentStep = 0, // Default for backward compatibility
  totalSteps = 1, // Default for backward compatibility
  onBack,
  onNext,
  canGoBack = true,
  canGoNext = true,
  isNextDisabled = false, // Backward compatibility
  nextLabel = 'Next',
  backLabel = 'Back',
  isNextLoading = false,
  isDarkMode: propIsDarkMode, // Backward compatibility
  className = ''
}) => {
  const { theme } = useTheme();
  const isDarkMode = propIsDarkMode !== undefined ? propIsDarkMode : theme === 'dark';
  
  // No need for complex sidebar tracking - let it flow naturally
  
  // Use isNextDisabled if provided, otherwise use canGoNext
  const nextDisabled = isNextDisabled || !canGoNext;

  // Determine if this is the last step
  const isLastStep = currentStep === totalSteps - 1;
  const isFirstStep = currentStep === 0;
  
  // Show progress only if we have valid step data
  const showProgress = totalSteps > 1;

  // Final step gets different label
  const finalNextLabel = isLastStep ? 'Complete Registration' : nextLabel;

  return (
    <div className={`step-navigation-container mt-8 ${className}`}>
      <div className="w-full">
        {isDarkMode ? (
          <GlowingCard
            glowColor="from-blue-600/20 via-purple-600/20 to-violet-600/20"
            glowIntensity="blur-lg"
            cardOpacity="bg-[#1a1f2d]/95"
            className="backdrop-blur-sm border shadow-lg"
          >
            <div className="p-3 sm:p-4">
              <div className="flex items-center justify-between">
                {/* Back Button */}
          <button
            onClick={onBack}
                  disabled={!canGoBack || isFirstStep}
                  className={`
                    flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-200
                    ${!canGoBack || isFirstStep
                      ? 'opacity-50 cursor-not-allowed bg-[var(--background-elevated)] text-[var(--text-tertiary)]'
                      : 'bg-[var(--background-elevated)] hover:bg-[var(--background-hover)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
                    }
                  `}
                >
                  <ChevronLeft className="h-4 w-4" />
                  <span className="font-medium">{backLabel}</span>
          </button>

                {/* Step Progress - only show if we have multiple steps */}
                {showProgress && (
                  <div className="flex items-center gap-2 text-sm text-[var(--text-secondary)]">
                    <span>Step {currentStep + 1} of {totalSteps}</span>
                    <div className="flex gap-1">
                      {Array.from({ length: totalSteps }).map((_, index) => (
                        <div
                          key={index}
                          className={`
                            w-2 h-2 rounded-full transition-colors duration-200
                            ${index <= currentStep
                              ? 'bg-[var(--color-blue-primary)]'
                              : 'bg-[var(--background-elevated)]'
                            }
                          `}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Next Button */}
                <button
                  onClick={onNext}
                  disabled={nextDisabled || isNextLoading}
                  className={`
                    flex items-center gap-2 px-6 py-2 rounded-lg font-medium transition-all duration-200
                    ${nextDisabled || isNextLoading
                      ? 'opacity-50 cursor-not-allowed bg-[var(--color-blue-primary)]/50 text-[var(--text-primary)]'
                      : isLastStep
                        ? 'bg-[var(--color-success)] hover:bg-[var(--color-success)]/90 text-white shadow-lg'
                        : 'bg-[var(--color-blue-primary)] hover:bg-[var(--color-blue-primary)]/90 text-white shadow-lg'
                    }
                  `}
                >
                  {isNextLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Processing...</span>
                    </>
                  ) : (
                    <>
                      <span>{finalNextLabel}</span>
                      <ChevronRight className="h-4 w-4" />
                    </>
                  )}
                </button>
              </div>
            </div>
          </GlowingCard>
        ) : (
          <div className="bg-white/95 backdrop-blur-sm border border-gray-200 rounded-lg shadow-lg">
            <div className="p-3 sm:p-4">
              <div className="flex items-center justify-between">
                {/* Back Button */}
                <button
                  onClick={onBack}
                  disabled={!canGoBack || isFirstStep}
                  className={`
                    flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-200
                    ${!canGoBack || isFirstStep
                      ? 'opacity-50 cursor-not-allowed bg-gray-100 text-gray-400'
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-700 hover:text-gray-900'
                    }
                  `}
                >
                  <ChevronLeft className="h-4 w-4" />
                  <span className="font-medium">{backLabel}</span>
                </button>

                {/* Step Progress - only show if we have multiple steps */}
                {showProgress && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <span>Step {currentStep + 1} of {totalSteps}</span>
                    <div className="flex gap-1">
                      {Array.from({ length: totalSteps }).map((_, index) => (
                        <div
                          key={index}
                          className={`
                            w-2 h-2 rounded-full transition-colors duration-200
                            ${index <= currentStep
                              ? 'bg-primary-600'
                              : 'bg-gray-300'
                            }
                          `}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Next Button */}
                        <button
                  onClick={onNext}
                  disabled={nextDisabled || isNextLoading}
                  className={`
                    flex items-center gap-2 px-6 py-2 rounded-lg font-medium transition-all duration-200
                    ${nextDisabled || isNextLoading
                      ? 'opacity-50 cursor-not-allowed bg-primary-400 text-white'
                      : isLastStep
                        ? 'bg-green-600 hover:bg-green-700 text-white shadow-lg'
                        : 'bg-primary-600 hover:bg-primary-700 text-white shadow-lg'
                    }
                  `}
                >
                  {isNextLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Processing...</span>
                    </>
                  ) : (
                    <>
                      <span>{finalNextLabel}</span>
                      <ChevronRight className="h-4 w-4" />
                    </>
                  )}
        </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StepNavigationFooter;